import React from "react";
import { ArrowRight, Phone, MessageCircle, Calendar } from "lucide-react";
import { Button } from "../ui/button";
import { childcareData } from "../../data/mock";

const CallToAction = () => {
  const handleWhatsAppClick = (phone, name) => {
    const message = `Hello ${name}! I'm interested in booking a tour of Heathbourne House Childcare.`;
    const whatsappUrl = `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handlePhoneCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  return (
    <section id="contact" className="py-20 bg-slate-800 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main CTA */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Ready to Give Your Child the 
            <span className="text-blue-400"> Best Start?</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
            Join our caring community at Heathbourne House where every child receives personalized attention in our beautiful Georgian setting.
          </p>
          
          {/* Primary Actions */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <Button 
              onClick={() => window.location.href = '/contact'}
              className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-xl"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Book Your Tour
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button 
              onClick={() => handleWhatsAppClick(childcareData.contact.whatsapp.jasmin, 'Jasmin')}
              className="bg-green-500 hover:bg-green-600 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-xl"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              WhatsApp Us
            </Button>
          </div>
        </div>

        {/* Contact Options Grid - Both Jasmin and Liza Equal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-4xl mx-auto">
          
          {/* Jasmin Contact */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/20 hover:border-blue-300/50 transition-all hover:shadow-xl">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Phone className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">Jasmin Gale</h3>
            <p className="text-blue-300 mb-4">Co-Founder & Operations Manager</p>
            <div className="space-y-3">
              <button 
                onClick={() => handlePhoneCall(childcareData.contact.phones.primary)}
                className="block w-full text-blue-300 hover:text-blue-200 font-medium transition-colors py-2 px-4 rounded-full border border-blue-300 hover:bg-blue-300 hover:text-slate-800"
              >
                {childcareData.contact.phones.primary}
              </button>
              <button
                onClick={() => handleWhatsAppClick(childcareData.contact.whatsapp.jasmin, 'Jasmin')}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-full transition-colors"
              >
                WhatsApp Jasmin
              </button>
            </div>
          </div>

          {/* Liza Contact */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/20 hover:border-pink-300/50 transition-all hover:shadow-xl">
            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Phone className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">Liza Gale</h3>
            <p className="text-pink-300 mb-4">Co-Founder & Lead Childcare Provider</p>
            <div className="space-y-3">
              <button 
                onClick={() => handlePhoneCall(childcareData.contact.phones.secondary)}
                className="block w-full text-pink-300 hover:text-pink-200 font-medium transition-colors py-2 px-4 rounded-full border border-pink-300 hover:bg-pink-300 hover:text-slate-800"
              >
                {childcareData.contact.phones.secondary}
              </button>
              <button
                onClick={() => handleWhatsAppClick(childcareData.contact.whatsapp.liza, 'Liza')}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-full transition-colors"
              >
                WhatsApp Liza
              </button>
            </div>
          </div>
        </div>

        {/* Email Contact */}
        <div className="text-center mb-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 inline-block border border-white/20">
            <h4 className="text-lg font-bold mb-2">Email Enquiries</h4>
            <a 
              href={`mailto:${childcareData.contact.email}`}
              className="text-blue-300 hover:text-blue-200 font-medium transition-colors text-sm break-all"
            >
              {childcareData.contact.email}
            </a>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="text-center border-t border-white/20 pt-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-300">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Currently Accepting New Families</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span>Government Funding Available</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
              <span>Tours Available Daily</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;